import { ArrowDropDown, Search } from '@mui/icons-material'
import { Box, CircularProgress, IconButton, Stack, type TextFieldProps } from '@mui/material'
import {
  type ChangeEvent,
  type Dispatch,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useState,
} from 'react'
import type { LocationEditorActions, LocationEditorProps, LocationEditorState } from '../types'

export function useTextfieldProps(
  dispatch: Dispatch<LocationEditorActions>,
  state: LocationEditorState,
  props: LocationEditorProps,
) {
  const { slotProps = {}, label, disabled, error, helperText, loading, placeholder } = props
  const { textFieldValue, open } = state
  const [ref, setRef] = useState<HTMLInputElement | null>(null)

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: 'textfield:change', text: event.target.value, event })
    },
    // dispatch is a stable function, putting as a deps has no effect
    [dispatch],
  )

  const onBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      dispatch({ type: 'textfield:blur', event })
    },
    // dispatch is a stable function, putting as a deps has no effect
    [dispatch],
  )

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      dispatch({ type: 'textfield:keydown', event })
    },
    // dispatch is a stable function, putting as a deps has no effect
    [dispatch],
  )

  const onClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      // since disabled is not in the state we need to check it here
      // TODO: maybe we should add it to the state?
      if (disabled) return
      dispatch({ type: 'textfield:click', event })
    },
    // dispatch is a stable function, putting as a deps has no effect
    [dispatch, disabled],
  )

  const onClickArrowDropdown = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      dispatch({ type: 'arrowDropdown:click' })
    },
    [dispatch],
  )

  const endAdornment = (
    <Stack direction="row" alignItems="center" ml={1}>
      <IconButton size="small" onClick={onClickArrowDropdown} disabled={disabled}>
        <Box
          sx={{
            display: 'flex',
            rotate: open ? '180deg' : '0deg',
            transition: 'rotate 0.2s ease',
          }}
        >
          <ArrowDropDown />
        </Box>
      </IconButton>
    </Stack>
  )

  const startAdornment = loading ? <Spinner /> : <Search sx={{ mr: 1 }} />

  const textFieldProps: TextFieldProps = {
    ...slotProps?.textfield,
    label,
    onChange,
    onKeyDown,
    onClick,
    onBlur,
    fullWidth: true,
    multiline: true,
    value: textFieldValue,
    disabled,
    error,
    helperText,
    placeholder,
    inputRef: setRef,

    InputProps: {
      startAdornment,
      endAdornment,
    },
  }

  return {
    textFieldProps,
    textFieldContainerRef: ref?.parentElement ?? null,
  }
}

function Spinner() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" width={24} height={24} mr={1}>
      <CircularProgress size={16} />
    </Box>
  )
}
